import { useMemo, useState, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import {
  Loader2,
  CreditCard,
  TrendingUp,
  Sparkles,
  UserPlus,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  Receipt,
  Download,
  FileText,
  FileSpreadsheet,
  CheckCircle2,
} from "lucide-react";

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";

import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useMyTier } from "@/hooks/use-my-tier";

// ── Types mirroring the /api/account-billing/* responses ─────────────
type LineItemCategory =
  | "plan"
  | "lite_revenue_share"
  | "lead_credits"
  | "ai_credits"
  | "storage"
  | "additional_users"
  | "other";

interface BillingLineItem {
  id: string;
  date: string;
  category: LineItemCategory;
  description: string;
  quantity: number | null;
  amountCents: number;
  status: string | null;
  stripePaymentIntentId: string | null;
  stripeInvoiceId: string | null;
}

interface SummaryResponse {
  currentPlan: {
    tier: string;
    displayName: string;
    billingCycle: "monthly" | "yearly";
    basePriceCents: number;
    priceSource?: "override" | "subscribed" | "tier";
    hasOverride?: boolean;
    nextBillDate: string | null;
    stripeCustomerId: string | null;
    stripeSubscriptionId: string | null;
  };
  liteRevenueShare: {
    currentMonth: {
      periodStart: string;
      periodEnd: string;
      trackedRevenueCents: number;
      sharePercent: number;
      projectedChargeCents: number;
    };
    runs: Array<{
      id: string;
      periodStart: string;
      periodEnd: string;
      trackedRevenueCents: number;
      sharePercent: number;
      amountChargedCents: number;
      status: string;
      stripeInvoiceId: string | null;
      stripePaymentIntentId: string | null;
      errorMessage: string | null;
      createdAt: string | null;
    }>;
  } | null;
  addOns: {
    leadGen: BillingLineItem[];
    aiTools: BillingLineItem[];
    storage: BillingLineItem[];
    additionalUsers: BillingLineItem[];
  };
}

interface StatementsResponse {
  statements: Array<{
    month: string;
    label: string;
    periodStart: string;
    periodEnd: string;
    planFeeCents: number;
    liteRevShareCents: number;
    addOnsCents: number;
    totalCents: number;
    lineItems: BillingLineItem[];
  }>;
}

interface AvailableTier {
  name: string;
  displayName: string;
  description: string | null;
  monthlyPriceCents: number | null;
  yearlyPriceCents: number | null;
}

// ── Helpers ──────────────────────────────────────────────────────────
function formatDollars(cents: number): string {
  const sign = cents < 0 ? "-" : "";
  const abs = Math.abs(cents);
  return `${sign}$${(abs / 100).toFixed(2)}`;
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function statusBadgeVariant(status: string | null): "default" | "secondary" | "destructive" | "outline" {
  if (!status) return "outline";
  if (status === "charged" || status === "paid" || status === "billed") return "default";
  if (status === "failed") return "destructive";
  if (status.startsWith("skipped")) return "secondary";
  return "outline";
}

function categoryLabel(c: LineItemCategory): string {
  switch (c) {
    case "plan": return "Plan";
    case "lite_revenue_share": return "Revenue share";
    case "lead_credits": return "Lead credits";
    case "ai_credits": return "AI assist";
    case "storage": return "Storage";
    case "additional_users": return "Seats";
    default: return "Other";
  }
}

// ── The page ─────────────────────────────────────────────────────────
export default function AccountBillingPage() {
  const { toast } = useToast();
  const { data: myTier, isLoading: tierLoading } = useMyTier();
  const workspaceId = myTier?.workspaceId || undefined;
  const isAdmin = (myTier?.role || "").toLowerCase() === "admin";

  // The server routes accept the workspace via `?workspaceId=…` query
  // string, not a path param, so we put the full URL (with query) in the
  // queryKey. The default fetcher does `queryKey.join("/")`, which would
  // otherwise produce `/api/account-billing/summary/<uuid>` — a 404 that
  // falls through to the SPA HTML and breaks JSON parsing.
  const summaryUrl = workspaceId
    ? `/api/account-billing/summary?workspaceId=${encodeURIComponent(workspaceId)}`
    : null;
  const statementsUrl = workspaceId
    ? `/api/account-billing/statements?workspaceId=${encodeURIComponent(workspaceId)}`
    : null;

  const summaryQuery = useQuery<SummaryResponse>({
    queryKey: [summaryUrl],
    enabled: !!summaryUrl && isAdmin,
  });

  const statementsQuery = useQuery<StatementsResponse>({
    queryKey: [statementsUrl],
    enabled: !!statementsUrl && isAdmin,
  });

  const [pickerOpen, setPickerOpen] = useState(false);
  const [addOnDateRange, setAddOnDateRange] = useState<AddOnDateRange>("all");

  const summaryData = summaryQuery.data;

  const filteredAddOns = useMemo(() => {
    const keep = (it: BillingLineItem) => isInAddOnRange(it.date, addOnDateRange);
    return {
      leadGen: (summaryData?.addOns.leadGen ?? []).filter(keep),
      aiTools: (summaryData?.addOns.aiTools ?? []).filter(keep),
      storage: (summaryData?.addOns.storage ?? []).filter(keep),
      additionalUsers: (summaryData?.addOns.additionalUsers ?? []).filter(keep),
    };
  }, [summaryData, addOnDateRange]);

  const portalMutation = useMutation({
    mutationFn: async () => {
      const r = await apiRequest("POST", "/api/account-billing/portal", { workspaceId });
      const j = await r.json();
      if (!j.url) throw new Error("No portal URL returned.");
      window.location.href = j.url;
    },
    onError: (e: any) =>
      toast({ title: "Couldn't open billing portal", description: String(e?.message || e), variant: "destructive" }),
  });

  const checkoutMutation = useMutation({
    mutationFn: async (vars: { tierName: string; billingPeriod: "monthly" | "yearly" }) => {
      const r = await apiRequest("POST", "/api/account-billing/checkout", {
        workspaceId,
        tierName: vars.tierName,
        billingPeriod: vars.billingPeriod,
      });
      const j = await r.json();
      if (j.url) {
        window.location.href = j.url;
        return;
      }
      // Existing-subscription update path: refresh and toast.
      if (j.updatedExisting) {
        toast({
          title: "Plan updated",
          description: `Switched to ${j.displayName || j.tier} (${j.billingPeriod}).`,
        });
        setPickerOpen(false);
        summaryQuery.refetch();
        statementsQuery.refetch();
        return;
      }
      throw new Error("No checkout URL returned.");
    },
    onError: (e: any) =>
      toast({ title: "Couldn't start checkout", description: String(e?.message || e), variant: "destructive" }),
  });

  if (tierLoading) {
    return (
      <div className="flex items-center justify-center py-16" data-testid="loading-account-billing">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-12" data-testid="page-account-billing-forbidden">
        <Alert variant="destructive">
          <AlertTitle>Admins only</AlertTitle>
          <AlertDescription>
            Account billing is only available to workspace admins. Please contact your workspace admin if you need to manage billing.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  if (summaryQuery.isLoading || statementsQuery.isLoading) {
    return (
      <div className="flex items-center justify-center py-16" data-testid="loading-account-billing">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (summaryQuery.error) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-12" data-testid="error-account-billing">
        <Alert variant="destructive">
          <AlertTitle>Couldn't load billing</AlertTitle>
          <AlertDescription>{(summaryQuery.error as any)?.message || "Unexpected error"}</AlertDescription>
        </Alert>
      </div>
    );
  }

  const summary = summaryQuery.data;
  const statements = statementsQuery.data?.statements || [];

  return (
    <div className="max-w-6xl mx-auto px-4 py-8 space-y-6" data-testid="page-account-billing">
      <header className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="space-y-1">
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Receipt className="h-7 w-7 text-muted-foreground" />
            Account Billing
          </h1>
          <p className="text-muted-foreground">
            Every charge from Clarik in one place.
          </p>
        </div>
      </header>

      {/* Current Plan card */}
      {summary && (
        <Card data-testid="card-current-plan">
          <CardHeader>
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <CreditCard className="h-5 w-5 text-muted-foreground" />
                  Current plan
                </CardTitle>
                <CardDescription>
                  Your active Clarik subscription.
                </CardDescription>
              </div>
              <Button
                onClick={() => {
                  if (summary.currentPlan.stripeSubscriptionId) {
                    portalMutation.mutate();
                  } else {
                    setPickerOpen(true);
                  }
                }}
                disabled={portalMutation.isPending}
                data-testid="button-manage-plan"
              >
                {portalMutation.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Opening…
                  </>
                ) : summary.currentPlan.stripeSubscriptionId ? (
                  <>
                    Manage plan
                    <ExternalLink className="h-4 w-4 ml-2" />
                  </>
                ) : (
                  <>Choose a plan</>
                )}
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <div className="text-xs uppercase tracking-wide text-muted-foreground">Plan</div>
                <div className="text-lg font-semibold mt-1" data-testid="text-current-tier">
                  {summary.currentPlan.displayName}
                </div>
                <Badge variant="outline" className="mt-1 capitalize">
                  {summary.currentPlan.billingCycle}
                </Badge>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wide text-muted-foreground">Base price</div>
                <div className="text-lg font-semibold mt-1" data-testid="text-base-price">
                  {summary.currentPlan.basePriceCents > 0
                    ? `${formatDollars(summary.currentPlan.basePriceCents)} / ${summary.currentPlan.billingCycle === "monthly" ? "mo" : "yr"}`
                    : summary.currentPlan.tier === "lite"
                      ? "Free + revenue share"
                      : "—"}
                </div>
                {summary.currentPlan.hasOverride && (
                  <Badge variant="secondary" className="mt-1 text-xs">
                    Custom pricing
                  </Badge>
                )}
              </div>
              <div>
                <div className="text-xs uppercase tracking-wide text-muted-foreground">Next bill</div>
                <div className="text-lg font-semibold mt-1" data-testid="text-next-bill">
                  {formatDate(summary.currentPlan.nextBillDate)}
                </div>
              </div>
            </div>
            {!summary.currentPlan.stripeSubscriptionId && (
              <Alert className="mt-4">
                <AlertDescription>
                  No active subscription yet. Click <strong>Choose a plan</strong> to start a paid plan and unlock the full customer portal.
                </AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>
      )}

      {/* Lite Revenue Share — only on lite tier */}
      {summary?.liteRevenueShare && (
        <Card data-testid="card-lite-revenue-share">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-muted-foreground" />
              Lite revenue share
            </CardTitle>
            <CardDescription>
              You pay {summary.liteRevenueShare.currentMonth.sharePercent}% of tracked Stripe Connect revenue, billed monthly.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 p-4 rounded-lg bg-muted/40">
              <div>
                <div className="text-xs uppercase tracking-wide text-muted-foreground">This month's tracked revenue</div>
                <div className="text-xl font-semibold mt-1" data-testid="text-tracked-revenue">
                  {formatDollars(summary.liteRevenueShare.currentMonth.trackedRevenueCents)}
                </div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wide text-muted-foreground">Share rate</div>
                <div className="text-xl font-semibold mt-1" data-testid="text-share-rate">
                  {summary.liteRevenueShare.currentMonth.sharePercent}%
                </div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wide text-muted-foreground">Projected charge</div>
                <div className="text-xl font-semibold mt-1" data-testid="text-projected-charge">
                  {formatDollars(summary.liteRevenueShare.currentMonth.projectedChargeCents)}
                </div>
              </div>
            </div>

            {summary.liteRevenueShare.runs.length > 0 ? (
              <div className="overflow-x-auto">
                <Table data-testid="table-lite-runs">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Period</TableHead>
                      <TableHead className="text-right">Tracked revenue</TableHead>
                      <TableHead className="text-right">Rate</TableHead>
                      <TableHead className="text-right">Charged</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {summary.liteRevenueShare.runs.map((run) => (
                      <TableRow key={run.id} data-testid={`row-lite-run-${run.id}`}>
                        <TableCell className="font-medium">
                          {new Date(run.periodStart).toLocaleString("en-US", { month: "long", year: "numeric", timeZone: "UTC" })}
                        </TableCell>
                        <TableCell className="text-right">{formatDollars(run.trackedRevenueCents)}</TableCell>
                        <TableCell className="text-right">{run.sharePercent}%</TableCell>
                        <TableCell className="text-right font-semibold">{formatDollars(run.amountChargedCents)}</TableCell>
                        <TableCell>
                          <Badge variant={statusBadgeVariant(run.status)} className="capitalize">
                            {run.status.replace(/_/g, " ")}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No revenue share charges yet.</p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Add-on charges */}
      {summary && (
        <Card data-testid="card-addons">
          <CardHeader>
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
              <div>
                <CardTitle>Add-on charges</CardTitle>
                <CardDescription>
                  Optional purchases on top of your plan: lead credits, AI credits, and additional seats.
                </CardDescription>
              </div>
              <Select value={addOnDateRange} onValueChange={(v) => setAddOnDateRange(v as AddOnDateRange)}>
                <SelectTrigger className="w-[160px] shrink-0" data-testid="select-addon-date-range">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all" data-testid="option-addon-range-all">All time</SelectItem>
                  <SelectItem value="today" data-testid="option-addon-range-today">Today</SelectItem>
                  <SelectItem value="last7" data-testid="option-addon-range-last7">Last 7 days</SelectItem>
                  <SelectItem value="thisMonth" data-testid="option-addon-range-this-month">This month</SelectItem>
                  <SelectItem value="thisYear" data-testid="option-addon-range-this-year">This year</SelectItem>
                  <SelectItem value="priorYears" data-testid="option-addon-range-prior-years">Prior years</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="leadGen" className="w-full">
              <TabsList className="flex w-full flex-wrap h-auto gap-1">
                <TabsTrigger value="leadGen" data-testid="tab-addon-leadgen">
                  <Sparkles className="h-3.5 w-3.5 mr-1.5" />
                  Lead credits
                  {filteredAddOns.leadGen.length > 0 && (
                    <Badge variant="secondary" className="ml-2">{filteredAddOns.leadGen.length}</Badge>
                  )}
                </TabsTrigger>
                <TabsTrigger value="aiTools" data-testid="tab-addon-ai">
                  <Sparkles className="h-3.5 w-3.5 mr-1.5" />
                  AI assist
                  {filteredAddOns.aiTools.length > 0 && (
                    <Badge variant="secondary" className="ml-2">{filteredAddOns.aiTools.length}</Badge>
                  )}
                </TabsTrigger>

                <TabsTrigger value="seats" data-testid="tab-addon-seats">
                  <UserPlus className="h-3.5 w-3.5 mr-1.5" />
                  Seats
                  {filteredAddOns.additionalUsers.length > 0 && (
                    <Badge variant="secondary" className="ml-2">{filteredAddOns.additionalUsers.length}</Badge>
                  )}
                </TabsTrigger>
              </TabsList>
              <TabsContent value="leadGen" className="mt-4">
                <AddOnTable items={filteredAddOns.leadGen} emptyText={addOnEmptyText("No lead credit purchases yet.", addOnDateRange)} testId="table-addon-leadgen" />
              </TabsContent>
              <TabsContent value="aiTools" className="mt-4">
                <AddOnTable items={filteredAddOns.aiTools} emptyText={addOnEmptyText("No paid AI assist credits yet.", addOnDateRange)} testId="table-addon-ai" />
              </TabsContent>

              <TabsContent value="seats" className="mt-4">
                <AddOnTable items={filteredAddOns.additionalUsers} emptyText={addOnEmptyText("No additional seats — you're within your plan limit.", addOnDateRange)} testId="table-addon-seats" />
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      )}

      {/* Plan picker dialog — shown when admin clicks "Choose a plan" */}
      <PlanPickerDialog
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        workspaceId={workspaceId}
        currentTier={summary?.currentPlan.tier || null}
        isPending={checkoutMutation.isPending}
        onSelect={(tierName, billingPeriod) =>
          checkoutMutation.mutate({ tierName, billingPeriod })
        }
      />

      {/* Monthly statements */}
      <Card data-testid="card-statements">
        <CardHeader>
          <CardTitle>Monthly statements</CardTitle>
          <CardDescription>
            Last 12 months. Click a row to see line items, or download a PDF or CSV for bookkeeping.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {statements.length === 0 ? (
            <p className="text-sm text-muted-foreground">No statements yet.</p>
          ) : (
            <div className="space-y-2">
              {statements.map((s) => (
                <StatementRow key={s.month} statement={s} workspaceId={workspaceId} />
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ── Add-on date-range filter helpers ─────────────────────────────────

type AddOnDateRange = "all" | "today" | "last7" | "thisMonth" | "thisYear" | "priorYears";

function isInAddOnRange(dateIso: string, range: AddOnDateRange): boolean {
  if (range === "all") return true;
  const d = new Date(dateIso);
  if (Number.isNaN(d.getTime())) return false;
  const now = new Date();
  switch (range) {
    case "today": {
      return d.getFullYear() === now.getFullYear() &&
        d.getMonth() === now.getMonth() &&
        d.getDate() === now.getDate();
    }
    case "last7": {
      const start = new Date(now);
      start.setDate(start.getDate() - 7);
      start.setHours(0, 0, 0, 0);
      return d >= start && d <= now;
    }
    case "thisMonth":
      return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
    case "thisYear":
      return d.getFullYear() === now.getFullYear();
    case "priorYears":
      return d.getFullYear() < now.getFullYear();
  }
}

function addOnEmptyText(baseEmpty: string, range: AddOnDateRange): string {
  if (range === "all") return baseEmpty;
  const label: Record<Exclude<AddOnDateRange, "all">, string> = {
    today: "today",
    last7: "in the last 7 days",
    thisMonth: "this month",
    thisYear: "this year",
    priorYears: "in prior years",
  };
  return `No charges ${label[range]}.`;
}

// ── Sub-components ────────────────────────────────────────────────────

function AddOnTable({
  items,
  emptyText,
  testId,
  hideAmount,
}: {
  items: BillingLineItem[];
  emptyText: string;
  testId: string;
  hideAmount?: boolean;
}) {
  if (items.length === 0) {
    return <p className="text-sm text-muted-foreground py-4">{emptyText}</p>;
  }
  return (
    <div className="overflow-x-auto">
      <Table data-testid={testId}>
        <TableHeader>
          <TableRow>
            <TableHead>Date</TableHead>
            <TableHead>Description</TableHead>
            <TableHead className="text-right">Quantity</TableHead>
            {!hideAmount && <TableHead className="text-right">Amount</TableHead>}
            <TableHead>Status</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.map((item) => (
            <TableRow key={item.id} data-testid={`row-addon-${item.id}`}>
              <TableCell className="whitespace-nowrap">{formatDate(item.date)}</TableCell>
              <TableCell>{item.description}</TableCell>
              <TableCell className="text-right">{item.quantity ?? "—"}</TableCell>
              {!hideAmount && (
                <TableCell className="text-right font-semibold">
                  {item.amountCents > 0 ? formatDollars(item.amountCents) : "—"}
                </TableCell>
              )}
              <TableCell>
                {item.status ? (
                  <Badge variant={statusBadgeVariant(item.status)} className="capitalize">
                    {item.status.replace(/_/g, " ")}
                  </Badge>
                ) : "—"}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function StatementRow({
  statement,
  workspaceId,
}: {
  statement: StatementsResponse["statements"][number];
  workspaceId: string | undefined;
}) {
  const [open, setOpen] = useState(false);
  const { toast } = useToast();
  const [downloading, setDownloading] = useState<null | "pdf" | "csv">(null);
  const lineItems = useMemo(() => statement.lineItems, [statement.lineItems]);
  const isEmpty = statement.totalCents === 0 && lineItems.length === 0;

  // Download flows trigger a binary fetch — we can't just use a plain <a>
  // because the endpoint requires the session cookie and a `workspaceId`
  // query param the rest of the page already passes. Streaming into a Blob
  // also lets us surface a friendly error toast on 4xx/5xx responses
  // instead of dropping the user into a broken file.
  const handleDownload = async (kind: "pdf" | "csv") => {
    if (!workspaceId || downloading) return;
    setDownloading(kind);
    try {
      const params = new URLSearchParams({ workspaceId });
      const res = await fetch(
        `/api/account-billing/statements/${encodeURIComponent(statement.month)}/${kind}?${params.toString()}`,
        { credentials: "include" },
      );
      if (!res.ok) {
        let message = `Download failed (${res.status})`;
        try {
          const j = await res.json();
          if (j?.message) message = j.message;
        } catch {
          // not JSON — keep generic message
        }
        throw new Error(message);
      }
      const blob = await res.blob();
      const disposition = res.headers.get("Content-Disposition") || "";
      const match = disposition.match(/filename="?([^";]+)"?/i);
      const filename = match?.[1] || `statement-${statement.month}.${kind}`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      // Give the browser a tick to start the download before revoking.
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err: any) {
      toast({
        title: "Couldn't download statement",
        description: String(err?.message || err),
        variant: "destructive",
      });
    } finally {
      setDownloading(null);
    }
  };

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <div className="flex items-stretch gap-2">
        <CollapsibleTrigger asChild>
          <button
            type="button"
            className="flex-1 flex items-center justify-between gap-3 px-4 py-3 rounded-lg border bg-card hover-elevate active-elevate-2 text-left"
            data-testid={`button-statement-${statement.month}`}
          >
            <div className="flex items-center gap-3 min-w-0">
              {open ? (
                <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />
              ) : (
                <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
              )}
              <div className="min-w-0">
                <div className="font-medium truncate">{statement.label}</div>
                {!isEmpty && (
                  <div className="text-xs text-muted-foreground truncate">
                    {statement.planFeeCents > 0 && <>Plan {formatDollars(statement.planFeeCents)} · </>}
                    {statement.liteRevShareCents > 0 && <>Rev share {formatDollars(statement.liteRevShareCents)} · </>}
                    {statement.addOnsCents > 0 && <>Add-ons {formatDollars(statement.addOnsCents)}</>}
                    {isEmpty && "No charges"}
                  </div>
                )}
              </div>
            </div>
            <div className="text-right shrink-0">
              <div className="font-semibold" data-testid={`text-statement-total-${statement.month}`}>
                {formatDollars(statement.totalCents)}
              </div>
            </div>
          </button>
        </CollapsibleTrigger>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className="self-stretch px-3"
              disabled={!!downloading || !workspaceId}
              data-testid={`button-download-statement-${statement.month}`}
              aria-label={`Download statement for ${statement.label}`}
            >
              {downloading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Download className="h-4 w-4" />
              )}
              <span className="sr-only sm:not-sr-only sm:ml-2 text-sm">Download</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem
              onSelect={(e) => {
                e.preventDefault();
                void handleDownload("pdf");
              }}
              data-testid={`menu-download-pdf-${statement.month}`}
            >
              <FileText className="h-4 w-4 mr-2" />
              Download PDF
            </DropdownMenuItem>
            <DropdownMenuItem
              onSelect={(e) => {
                e.preventDefault();
                void handleDownload("csv");
              }}
              data-testid={`menu-download-csv-${statement.month}`}
            >
              <FileSpreadsheet className="h-4 w-4 mr-2" />
              Download CSV
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <CollapsibleContent className="px-4 pt-3 pb-4">
        {lineItems.length === 0 ? (
          <p className="text-sm text-muted-foreground">No charges this month.</p>
        ) : (
          <div className="overflow-x-auto">
            <Table data-testid={`table-statement-lines-${statement.month}`}>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {lineItems.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell className="whitespace-nowrap">{formatDate(item.date)}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{categoryLabel(item.category)}</Badge>
                    </TableCell>
                    <TableCell>{item.description}</TableCell>
                    <TableCell className="text-right font-semibold">{formatDollars(item.amountCents)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CollapsibleContent>
    </Collapsible>
  );
}

function PlanPickerDialog({
  open,
  onOpenChange,
  workspaceId,
  currentTier,
  isPending,
  onSelect,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  workspaceId: string | undefined;
  currentTier: string | null;
  isPending: boolean;
  onSelect: (tierName: string, billingPeriod: "monthly" | "yearly") => void;
}) {
  const [billingPeriod, setBillingPeriod] = useState<"monthly" | "yearly">("monthly");
  const [selectedTier, setSelectedTier] = useState<string | null>(null);

  const tiersQuery = useQuery<AvailableTier[]>({
    queryKey: ["/api/account-billing/available-tiers", workspaceId],
    enabled: open && !!workspaceId,
  });

  const tierOrder = ["lite", "pro", "business", "enterprise"];
  const currentIdx = currentTier ? tierOrder.indexOf(currentTier) : -1;
  const tiers = (tiersQuery.data || []).filter((t) => {
    const idx = tierOrder.indexOf(t.name);
    if (currentIdx === -1 || idx === -1) return true;
    return idx >= currentIdx;
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="dialog-plan-picker">
        <DialogHeader>
          <DialogTitle>Choose a plan</DialogTitle>
          <DialogDescription>
            Pick a tier and billing cadence. You'll be redirected to Stripe to enter your card.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label className="text-sm font-medium">Billing cycle</Label>
            <RadioGroup
              value={billingPeriod}
              onValueChange={(v) => setBillingPeriod(v === "yearly" ? "yearly" : "monthly")}
              className="flex gap-4 mt-2"
            >
              <div className="flex items-center gap-2">
                <RadioGroupItem value="monthly" id="cycle-monthly" data-testid="radio-cycle-monthly" />
                <Label htmlFor="cycle-monthly" className="cursor-pointer">Monthly</Label>
              </div>
              <div className="flex items-center gap-2">
                <RadioGroupItem value="yearly" id="cycle-yearly" data-testid="radio-cycle-yearly" />
                <Label htmlFor="cycle-yearly" className="cursor-pointer">Yearly</Label>
              </div>
            </RadioGroup>
          </div>

          {tiersQuery.isLoading ? (
            <div className="flex items-center justify-center py-8" data-testid="loading-tiers">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : tiersQuery.error ? (
            <Alert variant="destructive" data-testid="error-tiers">
              <AlertTitle>Couldn't load plans</AlertTitle>
              <AlertDescription>
                {(tiersQuery.error as any)?.message || "Unexpected error loading available plans."}{" "}
                <button
                  type="button"
                  className="underline"
                  onClick={() => tiersQuery.refetch()}
                  data-testid="button-retry-tiers"
                >
                  Try again
                </button>
              </AlertDescription>
            </Alert>
          ) : tiers.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4" data-testid="empty-tiers">
              No upgrade tiers available. Please contact admin@clarik.io.
            </p>
          ) : (
            <div className="grid gap-3">
              {tiers.map((t) => {
                const priceCents = billingPeriod === "yearly" ? t.yearlyPriceCents : t.monthlyPriceCents;
                const disabled = !priceCents || priceCents <= 0;
                const isSelected = selectedTier === t.name;
                return (
                  <button
                    key={t.name}
                    type="button"
                    disabled={disabled}
                    onClick={() => setSelectedTier(t.name)}
                    className={`w-full text-left rounded-lg border p-4 transition-colors hover-elevate active-elevate-2 ${
                      isSelected ? "border-primary ring-2 ring-primary/20" : "border-border"
                    } ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
                    data-testid={`button-pick-tier-${t.name}`}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <div className="font-semibold flex items-center gap-2">
                          {t.displayName}
                          {isSelected && <CheckCircle2 className="h-4 w-4 text-primary" />}
                        </div>
                        {t.description && (
                          <div className="text-sm text-muted-foreground truncate">{t.description}</div>
                        )}
                      </div>
                      <div className="text-right shrink-0">
                        <div className="font-semibold" data-testid={`text-tier-price-${t.name}`}>
                          {disabled
                            ? "—"
                            : `${formatDollars(priceCents!)} / ${billingPeriod === "yearly" ? "yr" : "mo"}`}
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isPending} data-testid="button-cancel-picker">
            Cancel
          </Button>
          <Button
            onClick={() => selectedTier && onSelect(selectedTier, billingPeriod)}
            disabled={!selectedTier || isPending}
            data-testid="button-confirm-picker"
          >
            {isPending ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Opening Stripe…
              </>
            ) : (
              <>Continue to checkout</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
